// Client-side wrapper around Google reCAPTCHA v3.
//
// Loaded once per SPA session (idempotent — repeated `useRecaptcha`
// hook calls share the same script tag). Exposes `getToken(action)`
// which returns a fresh token bound to `action` (must match the
// `action=<slug>` the backend `verify_captcha` call passes to Google
// or Google rejects the token).
//
// If `REACT_APP_RECAPTCHA_SITE_KEY` is empty (dev / preview), the
// widget is skipped and `getToken` returns null — the backend also
// no-ops verification in that mode so form submits still succeed.

import { useEffect, useRef, useState, useCallback } from "react";

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";
const SCRIPT_ID = "cpg-recaptcha-v3";

// Track script load promise so 10 hooks mounting at once still hit the
// CDN only once.
let _loadPromise = null;

function loadRecaptcha() {
  if (!SITE_KEY) return Promise.resolve(false);
  if (_loadPromise) return _loadPromise;
  _loadPromise = new Promise((resolve) => {
    if (typeof window === "undefined") return resolve(false);
    if (window.grecaptcha && typeof window.grecaptcha.execute === "function") {
      return resolve(true);
    }
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => resolve(false));
      return;
    }
    const s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(SITE_KEY)}`;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
  });
  return _loadPromise;
}

export function useRecaptcha() {
  const [ready, setReady] = useState(false);
  const readyRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    loadRecaptcha().then((ok) => {
      if (cancelled) return;
      readyRef.current = ok;
      setReady(ok);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const getToken = useCallback(async (action) => {
    if (!SITE_KEY) return null; // preview / dev with no key configured
    if (!readyRef.current && typeof window !== "undefined") {
      // The user may have submitted before the CDN script finished
      // loading. Await once more.
      const ok = await loadRecaptcha();
      readyRef.current = ok;
      if (!ok) return null;
    }
    try {
      // v3 tokens are single-use and expire in 2 minutes — call this
      // just before the network request, not at page load.
      const token = await window.grecaptcha.execute(SITE_KEY, { action });
      return token || null;
    } catch (_err) {
      return null;
    }
  }, []);

  return { ready, getToken, enabled: Boolean(SITE_KEY) };
}
